// ACCOUNT ON STOP, the client side (Session SC9B). One cache and one batcher behind the shared AccountSymbol: every symbol on a page asks for
// its customer, the asks made in the same moment go out as ONE request (at most 100 ids), and the answer is kept for a minute. The server
// derives the answer live from Xero (server/src/modules/invoices/accountOnStop.ts); nothing here decides anything. A request that fails reads
// "not_checked", never "up to date". Office screens only: no engineer or customer screen imports this (accountOnStopNeverLeaks.test.ts).
import { useEffect, useState } from "react";
import { api, type AccountStatusDto } from "./api";

export type { AccountStatusDto };

export const NOT_CHECKED_STATUS: AccountStatusDto = { onStop: "not_checked", overdueAmount: null, oldestDaysOverdue: null };

const TTL_MS = 60_000;
const FAIL_TTL_MS = 10_000;
const MAX_IDS = 100;

interface Entry {
  value: AccountStatusDto;
  at: number;
  ttl: number;
}
const cache = new Map<string, Entry>();
const waiting = new Map<string, Array<(v: AccountStatusDto) => void>>();
let timer: ReturnType<typeof setTimeout> | null = null;

export function clearAccountStatusCache(): void {
  cache.clear();
}

/** Seed the cache from a payload that already carries the status (the register page, the overview). */
export function primeAccountStatus(customerId: string, status: AccountStatusDto | null | undefined): void {
  if (status) cache.set(customerId, { value: status, at: Date.now(), ttl: TTL_MS });
}

async function flush(): Promise<void> {
  timer = null;
  const batch = [...waiting.entries()];
  waiting.clear();
  for (let i = 0; i < batch.length; i += MAX_IDS) {
    const chunk = batch.slice(i, i + MAX_IDS);
    const ids = chunk.map(([id]) => id);
    let found = new Map<string, AccountStatusDto>();
    let ok = true;
    try {
      const res = await api.customers.accountStatus(ids);
      found = new Map(res.items.map((it) => [it.customerId, { onStop: it.onStop, overdueAmount: it.overdueAmount, oldestDaysOverdue: it.oldestDaysOverdue }]));
    } catch {
      ok = false;
    }
    for (const [id, resolvers] of chunk) {
      const value = found.get(id) ?? NOT_CHECKED_STATUS;
      cache.set(id, { value, at: Date.now(), ttl: ok && found.has(id) ? TTL_MS : FAIL_TTL_MS });
      for (const r of resolvers) r(value);
    }
  }
}

/** The cached status when it is fresh, else null (a synchronous read, so a primed or already-fetched symbol paints at once). */
export function peekAccountStatus(customerId: string): AccountStatusDto | null {
  const hit = cache.get(customerId);
  return hit && Date.now() - hit.at < hit.ttl ? hit.value : null;
}

export function fetchAccountStatus(customerId: string): Promise<AccountStatusDto> {
  const hit = cache.get(customerId);
  if (hit && Date.now() - hit.at < hit.ttl) return Promise.resolve(hit.value);
  return new Promise((resolve) => {
    const list = waiting.get(customerId) ?? [];
    list.push(resolve);
    waiting.set(customerId, list);
    if (!timer) timer = setTimeout(() => void flush(), 0);
  });
}

/** The status of one customer, from the page's payload when it carries one, else from the batched read. null while it is being asked. */
export function useAccountStatus(customerId: string | null | undefined, inline?: AccountStatusDto | null): AccountStatusDto | null {
  const [fetched, setFetched] = useState<{ id: string; value: AccountStatusDto } | null>(null);
  const cached = customerId && !inline ? peekAccountStatus(customerId) : null;
  useEffect(() => {
    if (!customerId || inline || cached) return;
    let live = true;
    void fetchAccountStatus(customerId).then((v) => {
      if (live) setFetched({ id: customerId, value: v });
    });
    return () => {
      live = false;
    };
  }, [customerId, inline, cached]);
  if (!customerId) return null;
  if (inline) return inline;
  if (cached) return cached;
  return fetched && fetched.id === customerId ? fetched.value : null;
}

/** The sentence the symbol shows, its title and its accessible name (one wording; the server's accountStatusLine says the same). */
export function accountStatusLabel(s: AccountStatusDto): string {
  if (s.onStop === "not_checked") return "Account not checked";
  if (s.onStop === false) return "Account up to date";
  return `On stop: invoices totalling GBP ${(s.overdueAmount ?? 0).toFixed(2)} more than 30 days overdue, oldest ${s.oldestDaysOverdue ?? 0} days`;
}
